import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../notification/notification.service';

// ============================================================================
// Számlázás
//
// A szűrés és az összesítés SZERVER oldalon fut (GET orders/invoices/list):
// korábban a képernyő a legutóbbi 200 rendelést töltötte be, és a „hiányzik"
// számláló csak ezekre vonatkozott — így nem lehetett teljeskörűen ellenőrizni,
// melyik rendeléshez készült számla. A teljes körű kimutatás a
// GET orders/invoices/audit végpontról jön.
//
// Műveletek:
//  - Számla létrehozás / újragenerálás: POST orders/:id/invoice/generate
//  - Sztornó: POST orders/:id/invoice/cancel (megerősítéssel)
//  - Letöltés: GET orders/:id/invoice/download → download_url vagy document_url
// ============================================================================

type BillStatus = 'ok' | 'none' | 'storno';
type BillStatusFilter = 'Mind' | 'Van számla' | 'Hiányzik' | 'Sztornózva';
type BillDateFilter = 'Mind' | '7 nap' | '30 nap' | '90 nap' | '1 év';
type ServerStatus = 'all' | 'invoiced' | 'missing' | 'cancelled';

// A dátum-szűrő chipek → visszatekintés napokban ('Mind' = nincs alsó határ).
const BILL_DATE_FILTER_DAYS: Record<BillDateFilter, number | null> = {
  'Mind': null,
  '7 nap': 7,
  '30 nap': 30,
  '90 nap': 90,
  '1 év': 365,
};

const STATUS_TO_SERVER: Record<BillStatusFilter, ServerStatus> = {
  'Mind': 'all',
  'Van számla': 'invoiced',
  'Hiányzik': 'missing',
  'Sztornózva': 'cancelled',
};

interface InvoiceListResponse {
  items: any[];
  total: number;
  page: number;
  pageSize: number;
  summary: { total: number; invoiced: number; cancelled: number; missing: number };
  sources: Array<{ name: string; count: number }>;
}

export interface InvoiceAudit {
  total: number;
  invoiced: number;
  cancelled: number;
  missing: number;
  byProvider: { okoskonyvelom: number; billingo: number };
  bySource: Array<{ source: string; total: number; invoiced: number; cancelled: number; missing: number }>;
  byMonth: Array<{ month: string; total: number; missing: number }>;
  missingSample: any[];
  missingSampleTruncated: boolean;
}

interface BillRowVM {
  order: any;
  id: number;
  idLabel: string;
  name: string;
  src: string;
  srcInitial: string;
  srcColor: string;
  dateLabel: string;
  amtLabel: string;
  st: BillStatus;
  stLabel: string;
  stClass: string;
  canCreate: boolean;
  canDownload: boolean;
  canRegen: boolean;
  canStorno: boolean;
}

// Forrás badge színek (prototípus srcColors — a valós bolt nevekre igazítva,
// összhangban a Rendelések képernyő badge színeivel)
const SRC_COLORS: Record<string, string> = {
  'Etsy': '#B9834A',
  'Shopify': '#6E9663',
  'Jump': '#5E7FA8',
  'Jump and Hide': '#5E7FA8',
  'Manual': '#9A6E9E',
  'Manuál': '#9A6E9E',
};

@Component({
  selector: 'app-szamlazas',
  templateUrl: './szamlazas.component.html',
  styleUrl: './szamlazas.component.scss',
})
export class SzamlazasComponent implements OnInit, OnDestroy {
  fetching = false;
  loadFailed = false;

  rows: BillRowVM[] = [];
  total = 0;
  page = 0;
  readonly pageSize = 100;
  summary = { total: 0, invoiced: 0, cancelled: 0, missing: 0 };

  searchCtrl = new FormControl('');
  sourceFilter = 'Mind';
  sourceChips: string[] = ['Mind'];
  readonly statusChips: BillStatusFilter[] = ['Mind', 'Van számla', 'Hiányzik', 'Sztornózva'];
  statusFilter: BillStatusFilter = 'Mind';
  readonly dateChips: BillDateFilter[] = ['Mind', '7 nap', '30 nap', '90 nap', '1 év'];
  dateFilter: BillDateFilter = 'Mind';

  // Egyedi dátum-tartomány (naptárból). Ha bármelyik ki van töltve, ez
  // felülírja a chip-szűrőt (és a chipek inaktívak).
  customFrom: Date | null = null;
  customTo: Date | null = null;

  // --- Teljes körű ellenőrzés (audit) ---
  audit: InvoiceAudit | null = null;
  auditLoading = false;
  auditOpen = false;

  get customRangeActive(): boolean {
    return !!(this.customFrom || this.customTo);
  }

  // Kijelölés rendelés id szerint (a prototípus billSel-je indexre ment)
  sel: Record<number, boolean> = {};
  actionLoadingByOrderId: Record<number, boolean> = {};
  bulkCreating = false;

  private searchSub?: Subscription;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  ngOnInit() {
    this.searchSub = this.searchCtrl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        this.page = 0;
        this.fetchOrders();
      });
    this.fetchOrders();
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe();
  }

  // --------------------------------------------------------------------------
  // Lekérés — a szűrés és az összesítés is szerver oldalon fut
  // --------------------------------------------------------------------------

  fetchOrders() {
    this.fetching = true;
    this.loadFailed = false;
    this.http
      .get<InvoiceListResponse>(`orders/invoices/list?${this.buildQuery()}`)
      .subscribe((response) => {
        this.rows = (response?.items || []).map((order) => this.buildRow(order));
        this.total = response?.total || 0;
        this.page = response?.page ?? this.page;
        this.summary = response?.summary || { total: 0, invoiced: 0, cancelled: 0, missing: 0 };
        this.sourceChips = ['Mind', ...(response?.sources || []).map((source) => source.name)];
        if (!this.sourceChips.includes(this.sourceFilter)) {
          this.sourceFilter = 'Mind';
        }
        // A már nem betöltött rendelésekre mutató kijelölések eldobása
        const loadedIds = new Set(this.rows.map((row) => row.id));
        Object.keys(this.sel).forEach((key) => {
          if (!loadedIds.has(Number(key))) {
            delete this.sel[Number(key)];
          }
        });
        this.fetching = false;
      }, () => {
        this.rows = [];
        this.total = 0;
        this.fetching = false;
        this.loadFailed = true;
      });
  }

  private buildQuery(): string {
    const params = new URLSearchParams();
    // A '#4108...' formát is értjük: a szerver a receipt_id-t számként keresi
    const search = (this.searchCtrl.value || '').trim().replace(/^#/, '');
    if (search) {
      params.set('search', search);
    }
    if (this.customRangeActive) {
      // Egyedi tartomány: a kezdő nap elejétől a záró nap végéig.
      if (this.customFrom) {
        params.set('from', this.customFrom.toISOString());
      }
      if (this.customTo) {
        params.set('to', this.customTo.toISOString());
      }
    } else {
      const days = BILL_DATE_FILTER_DAYS[this.dateFilter];
      if (days !== null && days !== undefined) {
        const from = new Date();
        from.setDate(from.getDate() - days);
        params.set('from', from.toISOString());
      }
    }
    params.set('status', STATUS_TO_SERVER[this.statusFilter]);
    if (this.sourceFilter && this.sourceFilter !== 'Mind') {
      params.set('source', this.sourceFilter);
    }
    params.set('page', `${this.page}`);
    params.set('page_size', `${this.pageSize}`);
    return params.toString();
  }

  setSourceFilter(name: string) {
    if (this.sourceFilter === name) {
      return;
    }
    this.sourceFilter = name;
    this.page = 0;
    this.fetchOrders();
  }

  setStatusFilter(status: BillStatusFilter) {
    if (this.statusFilter === status) {
      return;
    }
    this.statusFilter = status;
    this.page = 0;
    this.fetchOrders();
  }

  setDateFilter(date: BillDateFilter) {
    if (this.dateFilter === date && !this.customRangeActive) {
      return;
    }
    // Chip választásakor töröljük az egyedi tartományt.
    this.customFrom = null;
    this.customTo = null;
    this.dateFilter = date;
    this.page = 0;
    this.fetchOrders();
  }

  // Naptáros dátum-tartomány (tól–ig) kezelése.
  dateInputValue(which: 'from' | 'to'): string {
    const value = which === 'from' ? this.customFrom : this.customTo;
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return '';
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  onDateInput(which: 'from' | 'to', event: Event) {
    const raw = (event.target as HTMLInputElement)?.value || '';
    const parsed = raw ? new Date(`${raw}T00:00:00`) : null;
    const value = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
    if (which === 'from') {
      this.customFrom = value;
    } else {
      // A záró napot a nap VÉGÉIG értjük, különben a kiválasztott nap
      // rendelései kimaradnának.
      if (value) {
        value.setHours(23, 59, 59, 999);
      }
      this.customTo = value;
    }
    this.page = 0;
    this.fetchOrders();
  }

  clearCustomRange() {
    this.customFrom = null;
    this.customTo = null;
    this.dateFilter = 'Mind';
    this.page = 0;
    this.fetchOrders();
  }

  // --------------------------------------------------------------------------
  // Lapozás
  // --------------------------------------------------------------------------

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get hasPrevPage(): boolean {
    return this.page > 0;
  }

  get hasNextPage(): boolean {
    return (this.page + 1) * this.pageSize < this.total;
  }

  prevPage() {
    if (!this.hasPrevPage) {
      return;
    }
    this.page--;
    this.fetchOrders();
  }

  nextPage() {
    if (!this.hasNextPage) {
      return;
    }
    this.page++;
    this.fetchOrders();
  }

  // --------------------------------------------------------------------------
  // Teljes körű ellenőrzés
  // --------------------------------------------------------------------------

  toggleAudit() {
    this.auditOpen = !this.auditOpen;
    if (this.auditOpen && !this.audit) {
      this.runAudit();
    }
  }

  runAudit() {
    if (this.auditLoading) {
      return;
    }
    this.auditLoading = true;
    // Az audit MINDEN rendelésre fut, függetlenül a képernyő szűrőitől — épp az
    // a kérdés, hogy a teljes múltban hol hiányzik számla.
    this.http.get<InvoiceAudit>('orders/invoices/audit').subscribe({
      next: (result) => {
        this.audit = result;
        this.auditLoading = false;
      },
      error: (err) => {
        this.auditLoading = false;
        this.notificationService.show(
          `Az ellenőrzés nem sikerült! Részletek: ${err.error?.message || err.message}`,
          'danger',
        );
      },
    });
  }

  /** A hiányzó számlák megnyitása a listában (a szűrők átállításával). */
  showMissingFromAudit(month?: string) {
    this.statusFilter = 'Hiányzik';
    this.sourceFilter = 'Mind';
    if (month) {
      const [year, monthIndex] = month.split('-').map((part) => Number(part));
      const from = new Date(year, monthIndex - 1, 1, 0, 0, 0, 0);
      const to = new Date(year, monthIndex, 0, 23, 59, 59, 999);
      this.customFrom = from;
      this.customTo = to;
    } else {
      this.customFrom = null;
      this.customTo = null;
      this.dateFilter = 'Mind';
    }
    this.page = 0;
    this.auditOpen = false;
    this.fetchOrders();
  }

  auditMonthLabel(month: string): string {
    const [year, monthIndex] = (month || '').split('-');
    return year && monthIndex ? `${year}. ${monthIndex}.` : month;
  }

  // --------------------------------------------------------------------------
  // Sor view-model a szerver válaszából
  // --------------------------------------------------------------------------

  private buildRow(order: any): BillRowVM {
    const st = this.getInvoiceStatus(order);
    const src = order?.source || 'Shop';
    return {
      order,
      id: Number(order?.id),
      idLabel: `#${order?.receipt_id}`,
      name: order?.customer || 'Ismeretlen vevő',
      src,
      srcInitial: (src || '?').charAt(0).toUpperCase(),
      srcColor: SRC_COLORS[src] || '#9AA098',
      dateLabel: this.formatOrderDate(order?.created_at),
      amtLabel: `${Math.round(Number(order?.price) || 0)} ${order?.currency_code || ''}`.trim(),
      st,
      stLabel: st === 'ok' ? 'Létrehozva' : (st === 'none' ? 'Hiányzik' : 'Sztornózva'),
      stClass: st === 'ok' ? 'bill-st--ok' : (st === 'none' ? 'bill-st--none' : 'bill-st--storno'),
      canCreate: st === 'none',
      canDownload: st === 'ok',
      canRegen: st === 'ok' || st === 'storno',
      canStorno: st === 'ok',
    };
  }

  private getInvoiceStatus(order: any): BillStatus {
    if (Number(order?.billingo_invoice_cancelled) === 1) {
      return 'storno';
    }
    return (order?.billingo_invoice_id || order?.billingo_invoice_download_url) ? 'ok' : 'none';
  }

  private formatOrderDate(value: unknown): string {
    if (!value) {
      return '–';
    }
    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return '–';
    }
    return formatDate(date, 'MMM dd', this.locale);
  }

  // --------------------------------------------------------------------------
  // Kijelölés
  // --------------------------------------------------------------------------

  get visibleRows(): BillRowVM[] {
    return this.rows;
  }

  toggleSel(row: BillRowVM) {
    this.sel[row.id] = !this.sel[row.id];
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every((row) => this.sel[row.id]);
  }

  toggleAll() {
    const target = !this.allSelected;
    this.rows.forEach((row) => {
      this.sel[row.id] = target;
    });
  }

  get selCount(): number {
    return Object.keys(this.sel).filter((key) => this.sel[Number(key)]).length;
  }

  private get selMissingRows(): BillRowVM[] {
    return this.rows.filter((row) => this.sel[row.id] && row.st === 'none');
  }

  get selMissing(): number {
    return this.selMissingRows.length;
  }

  /** Hiányzó számlák a TELJES szűrt halmazon (nem csak az aktuális oldalon). */
  get missingCount(): number {
    return this.summary.missing;
  }

  // --------------------------------------------------------------------------
  // Fejléc + lábléc feliratok
  // --------------------------------------------------------------------------

  get missingChipLabel(): string {
    if (this.summary.total === 0) {
      return 'Nincs rendelés a szűrésben';
    }
    if (this.summary.missing === 0) {
      return `Mind a(z) ${this.summary.total} rendeléshez van számla`;
    }
    return `${this.summary.missing} hiányzó számla (${this.summary.total} rendelésből)`;
  }

  get bulkLabel(): string {
    return this.selMissing > 0 ? `Számla létrehozása (${this.selMissing})` : 'Számla létrehozása';
  }

  get footerLabel(): string {
    const selection = this.selCount > 0 ? `${this.selCount} kijelölve` : 'nincs kijelölés';
    if (this.total === 0) {
      return `0 rendelés · ${selection}`;
    }
    const first = this.page * this.pageSize + 1;
    const last = Math.min(this.total, (this.page + 1) * this.pageSize);
    return `${first}–${last}. rendelés (összesen ${this.total}) · ${selection}`;
  }

  // --------------------------------------------------------------------------
  // Számla műveletek
  // --------------------------------------------------------------------------

  isRowLoading(row: BillRowVM): boolean {
    return !!this.actionLoadingByOrderId[row.id];
  }

  createInvoice(row: BillRowVM) {
    this.runInvoiceGeneration(row, 'Számla létrehozva.');
  }

  regenInvoice(row: BillRowVM) {
    this.confirmDialog.open({
      title: 'Számla újragenerálása',
      message: `Biztosan új számlát állítunk ki a(z) ${row.idLabel} rendeléshez?`,
      details: 'Az aktív számlázó új számlát készít a rendelés aktuális adataival.',
      confirmLabel: 'Újragenerálás',
      tone: 'primary',
      icon: 'fa-solid fa-file-invoice',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.runInvoiceGeneration(row, 'Számla újragenerálva.');
    });
  }

  private runInvoiceGeneration(row: BillRowVM, successMessage: string) {
    if (this.actionLoadingByOrderId[row.id]) {
      return;
    }
    this.actionLoadingByOrderId[row.id] = true;
    this.http.post(`orders/${row.id}/invoice/generate`, {}).subscribe((updatedOrder: any) => {
      this.actionLoadingByOrderId[row.id] = false;
      this.applyOrderUpdate(row, updatedOrder);
      this.notificationService.show(successMessage, 'success');
    }, (err) => {
      this.actionLoadingByOrderId[row.id] = false;
      this.notificationService.show(`Számlagenerálás sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  downloadInvoice(row: BillRowVM) {
    this.http.get<{ download_url: string | null; document_url?: string | null }>(
      `orders/${row.id}/invoice/download`,
    ).subscribe((res) => {
      if (res?.download_url) {
        window.open(res.download_url, '_blank');
        return;
      }
      // Az Okos Könyvelőm nem ad bejelentkezés nélkül megnyitható számlaképet:
      // a PDF-et a backend tölti le a kulccsal, mi blobként nyitjuk meg.
      if (res?.document_url) {
        this.openInvoiceDocument(row.id);
        return;
      }
      this.notificationService.show('Nincs letölthető számla URL.', 'warning');
    }, (err) => {
      this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  private openInvoiceDocument(orderId: number) {
    const previewWindow = window.open('', '_blank');
    this.http.get(`orders/${orderId}/invoice/document`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
      error: (err) => {
        previewWindow?.close();
        this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      },
    });
  }

  cancelInvoice(row: BillRowVM) {
    this.confirmDialog.open({
      title: 'Számla sztornózása',
      message: `A(z) ${row.idLabel} rendelés számlája sztornózásra kerül.`,
      details: 'Ez a művelet nem vonható vissza.',
      confirmLabel: 'Sztornózás',
      tone: 'danger',
      icon: 'fa-solid fa-file-circle-xmark',
    }).subscribe((result) => {
      if (!result?.confirmed || this.actionLoadingByOrderId[row.id]) {
        return;
      }
      this.actionLoadingByOrderId[row.id] = true;
      this.http.post(`orders/${row.id}/invoice/cancel`, {}).subscribe((updatedOrder: any) => {
        this.actionLoadingByOrderId[row.id] = false;
        this.applyOrderUpdate(row, updatedOrder);
        this.notificationService.show('Számla sztornózva.', 'success');
      }, (err) => {
        this.actionLoadingByOrderId[row.id] = false;
        this.notificationService.show(`Sztornó sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      });
    });
  }

  async bulkCreate() {
    const targets = this.selMissingRows;
    if (targets.length === 0 || this.bulkCreating) {
      return;
    }
    this.bulkCreating = true;
    let created = 0;
    let failed = 0;
    let firstError = '';
    // Szekvenciális hívás, hogy ne terheljük párhuzamos kérésekkel a számlázót
    for (const row of targets) {
      try {
        await firstValueFrom(this.http.post(`orders/${row.id}/invoice/generate`, {}));
        created++;
      } catch (err: any) {
        failed++;
        if (!firstError) {
          firstError = err?.error?.message || err?.message || 'ismeretlen hiba';
        }
      }
    }
    this.sel = {};
    this.bulkCreating = false;
    // Újratöltés: a szerver oldali státusz és az összesítő is frissül.
    this.fetchOrders();
    this.audit = null;
    if (failed === 0) {
      this.notificationService.show(`${created} számla létrehozva.`, 'success');
    } else if (created > 0) {
      this.notificationService.show(`${created} számla létrehozva, ${failed} sikertelen. Részletek: ${firstError}`, 'warning');
    } else {
      this.notificationService.show(`Számlagenerálás sikertelen! Részletek: ${firstError}`, 'danger');
    }
  }

  private applyOrderUpdate(row: BillRowVM, updatedOrder: any) {
    Object.assign(row.order, updatedOrder || {});
    const index = this.rows.indexOf(row);
    if (index >= 0) {
      this.rows[index] = this.buildRow(row.order);
    }
    // Az összesítő a szerverről jön — a művelet után újrakérjük, hogy a
    // „hiányzik" számláló ne csússzon el.
    this.refreshSummaryOnly();
    this.audit = null;
  }

  private refreshSummaryOnly() {
    this.http.get<InvoiceListResponse>(`orders/invoices/list?${this.buildQuery()}`).subscribe({
      next: (response) => {
        this.summary = response?.summary || this.summary;
        this.total = response?.total ?? this.total;
      },
      error: () => {},
    });
  }

  trackByRowId(_: number, row: BillRowVM) {
    return row.id;
  }
}
